/**
 * `TasksSource` over `@wtfalch/tasks`' client -- the real service through
 * `/_tasks`, or the SDK's own fake in the preview and in tests. Both are the
 * same `TasksClient` type, so this is the only mapping there is.
 */

import type {
  Issue,
  IssueListItem,
  StatusGroup as SdkStatusGroup,
  Source,
  Status,
} from '@wtfalch/tasks';
import { createTasksClient, type TasksClient } from '@wtfalch/tasks';

import { isIsoDay } from './dates.ts';
import {
  cleanText,
  type FromKind,
  MINE,
  type NewTask,
  NOTES_MAX,
  type Person,
  type Task,
  type TaskDetail,
  type TaskList,
  type TaskStatus,
  type TasksSource,
  TITLE_MAX,
} from './source.ts';

const TASKS_MOUNT = '/_tasks';

/** Every request to the tasks service gives up after this long. */
export const TASKS_TIMEOUT_MS = 15_000;

/** The browser's route to the tasks service: `/_tasks` on the agora's own
 *  origin (`tasksUrl`), with the member's bearer and a timeout on every call. */
export function createAgoraTasksClient(options: {
  origin: string;
  getAccessToken: () => string;
  fetch?: typeof fetch;
  timeoutMs?: number;
}): TasksClient {
  const doFetch = options.fetch ?? ((input, init) => fetch(input, init));
  const timeoutMs = options.timeoutMs ?? TASKS_TIMEOUT_MS;
  return createTasksClient({
    // The SDK resolves `/v1/...` against the base with `new URL`, which drops
    // any path on the base, so the `/_tasks` mount is put back here.
    baseUrl: options.origin,
    credential: options.getAccessToken,
    fetch: (input, init) => {
      const url = new URL(input instanceof Request ? input.url : String(input));
      url.pathname = `${TASKS_MOUNT}${url.pathname}`;
      const limit = AbortSignal.timeout(timeoutMs);
      const signal = init?.signal ? AbortSignal.any([init.signal, limit]) : limit;
      return doFetch(url, { ...init, signal });
    },
  });
}

const PERSONAL_STATUSES: readonly TaskStatus[] = [
  { id: 'personal:todo', name: 'To do', group: 'todo' },
  { id: 'personal:started', name: 'Doing', group: 'started' },
  { id: 'personal:done', name: 'Done', group: 'done' },
];

const FROM_KINDS: readonly string[] = [
  'chat.message',
  'chat.call',
  'mail.thread',
  'calendar.event',
];
const OPEN_GROUPS: SdkStatusGroup[] = ['backlog', 'todo', 'started'];
const isClosed = (group: string) => group === 'done' || group === 'cancelled';

function fromOf(sources: readonly Source[]): Task['from'] {
  const first = sources.find((s) => FROM_KINDS.includes(s.kind));
  if (!first) return null;
  const label = cleanText(first.label, TITLE_MAX);
  return label ? { kind: first.kind as FromKind, label } : null;
}

export type SdkTasksOptions = {
  /** Names for lists. The SDK knows a team only by its key; the company's
   *  directory knows its name. Without one the key is the name. */
  listNames?: Readonly<Record<string, string>>;
};

export function sdkTasks(client: TasksClient, options: SdkTasksOptions = {}): TasksSource {
  // Names the service has told us, by principal id: an issue carries only an
  // id, and the list it came in carries the names.
  const known = new Map<string, string>();
  const learn = (people: Record<string, string>) => {
    for (const [id, name] of Object.entries(people)) {
      const clean = cleanText(name, 80);
      if (clean) known.set(id, clean);
    }
  };
  let meId: Promise<string> | null = null;
  const me = async (): Promise<Person> => {
    meId ??= client.me().then((m) => m.principalId);
    try {
      const id = await meId;
      return { id, name: known.get(id) ?? 'You', me: true };
    } catch (e) {
      // A failed read must not stick: the next call asks again.
      meId = null;
      throw e;
    }
  };

  const statusCache = new Map<string, Promise<readonly TaskStatus[]>>();
  const statusesOf = (teamId: string | null) => {
    if (teamId === null) return Promise.resolve(PERSONAL_STATUSES);
    let hit = statusCache.get(teamId);
    if (!hit) {
      hit = client.teams
        .get(teamId)
        .then((t) => t.statuses.map((s: Status) => ({ id: s.id, name: s.name, group: s.group })));
      statusCache.set(teamId, hit);
      hit.catch(() => statusCache.delete(teamId));
    }
    return hit;
  };

  const personOf = (id: string | null, meId: string): Person | null =>
    id === null
      ? null
      : { id, name: known.get(id) ?? (id === meId ? 'You' : 'Someone'), me: id === meId };

  const toTask = async (issue: IssueListItem | Issue, meId: string): Promise<Task> => {
    const statuses = await statusesOf(issue.teamId);
    const status = statuses.find((s) => s.id === issue.statusId);
    return {
      id: issue.id,
      key: cleanText(issue.key, 24),
      title: cleanText(issue.title, TITLE_MAX),
      notes: issue.description ? cleanText(issue.description, NOTES_MAX) : null,
      done: status ? isClosed(status.group) : issue.completedAt !== null,
      dueOn: isIsoDay(issue.dueOn) ? issue.dueOn : null,
      listId: issue.teamId ?? MINE,
      statusId: issue.statusId,
      statusName: cleanText(status?.name, 60) || 'To do',
      assignee: personOf(issue.assigneeId, meId),
      from: fromOf(issue.sources),
      steps: issue.childCount,
      stepsDone: issue.childDoneCount,
    };
  };

  // The service's order is by whatever the query asked for; a screen wants the
  // order tasks were made in, so the same list reads the same way every time.
  const fold = async (issues: readonly IssueListItem[], people: Record<string, string>) => {
    const { id } = await me();
    learn(people);
    const made = [...issues].sort(
      (a, b) =>
        a.createdAt.localeCompare(b.createdAt) ||
        (a.teamId ?? '').localeCompare(b.teamId ?? '') ||
        a.number - b.number,
    );
    return Promise.all(made.map((i) => toTask(i, id)));
  };

  const source: TasksSource = {
    me,

    async lists() {
      const [teams, mine] = await Promise.all([client.teams.list(), source.tasks(MINE)]);
      const shared = await Promise.all(
        teams.teams.map(async (team) => {
          const [statuses, members, tasks] = await Promise.all([
            statusesOf(team.teamId),
            client.teams.members(team.teamId),
            source.tasks(team.teamId),
          ]);
          const { id: meId } = await me();
          learn(Object.fromEntries(members.members.map((m) => [m.id, m.name])));
          return {
            id: team.teamId,
            name: cleanText(options.listNames?.[team.teamId], 80) || cleanText(team.key, 8),
            members: members.members.map((m) => ({
              id: m.id,
              name: cleanText(m.name, 80) || m.id,
              me: m.id === meId,
            })),
            statuses,
            open: tasks.filter((t) => !t.done).length,
            done: tasks.filter((t) => t.done).length,
          } satisfies TaskList;
        }),
      );
      return [
        {
          id: MINE,
          name: 'My tasks',
          members: [],
          statuses: PERSONAL_STATUSES,
          open: mine.length,
          done: 0,
        },
        ...shared,
      ];
    },

    async tasks(listId) {
      if (listId === MINE) {
        const r = await client.issues.list({
          filter: { assignee: 'me', statusGroups: OPEN_GROUPS },
        });
        return fold(
          r.issues.filter((i) => i.parentId === null),
          r.people,
        );
      }
      const r = await client.issues.list({ filter: { teams: [listId] } });
      return fold(
        r.issues.filter((i) => i.parentId === null),
        r.people,
      );
    },

    async task(id) {
      const [issue, kids, { id: meId }] = await Promise.all([
        client.issues.get(id),
        client.issues.children(id),
        me(),
      ]);
      learn(kids.people);
      let task = await toTask(issue, meId);
      if (task.assignee?.name === 'Someone') {
        await source.tasks(task.listId);
        task = await toTask(issue, meId);
      }
      return {
        ...task,
        stepList: await Promise.all(
          [...kids.issues].sort((a, b) => a.number - b.number).map((k) => toTask(k, meId)),
        ),
        attachments: issue.attachments.map((a) => ({
          id: a.id,
          name: cleanText(a.name, TITLE_MAX),
        })),
      } satisfies TaskDetail;
    },

    async create(input: NewTask) {
      const { id: meId } = await me();
      const created = await client.issues.create({
        teamId: input.listId === MINE ? null : input.listId,
        title: input.title,
        assigneeId: meId,
        ...(input.dueOn ? { dueOn: input.dueOn } : {}),
        ...(input.from
          ? {
              sources: [
                { kind: input.from.kind, sourceId: input.from.sourceId, label: input.from.label },
              ],
            }
          : {}),
      });
      return toTask(created, meId);
    },

    async setDone(task, done) {
      const statuses = await statusesOf(task.listId === MINE ? null : task.listId);
      const target = done
        ? statuses.find((s) => s.group === 'done')
        : (statuses.find((s) => s.group === 'todo') ?? statuses.find((s) => s.group === 'started'));
      if (!target) throw new Error('This list has no status to move the task to.');
      return source.moveTo(task.id, target.id);
    },

    async moveTo(id, statusId) {
      const { id: meId } = await me();
      const updated = await client.issues.update(id, { statusId });
      return toTask(updated, meId);
    },

    async addStep(parentId, title) {
      const { id: meId } = await me();
      const parent = await client.issues.get(parentId);
      const created = await client.issues.create({
        teamId: parent.teamId,
        title,
        parentId,
        assigneeId: parent.assigneeId ?? meId,
      });
      return toTask(created, meId);
    },
  };
  return source;
}
